"use client";

import Link from "next/link";
import { FaFacebookF, FaXTwitter, FaLinkedinIn } from "react-icons/fa6";
import { tv, VariantProps } from "tailwind-variants";
import qs from "qs";
import { cn, withUrl } from "@/lib/utils";
import { usePathname } from "next/navigation";

const shareSocialsVariant = tv({
  base: "!size-6 !rounded-full !border !border-secondary-500 !p-1 !text-secondary-500 md:!size-8 md:!p-2",
  variants: {
    color: {
      teal: "!border-[#00797D] !text-[#00797D]",
      orange: "!border-[#C74601] !text-[#C74601]",
    },
  },
  defaultVariants: {
    color: "teal",
  },
});

type ShareSocialsVariant = VariantProps<typeof shareSocialsVariant>;

export interface ShareSocialsProps extends ShareSocialsVariant {
  title?: string;
  className?: string;
  hashtag?: string;
  shareText?: string;
}

/**
 * ShareSocials - Third-parties are finnicky, so let's resort to hardcoded URLs.
 *
 * TODO: Needs e2e tests to check if they work!
 */

const ShareSocials: React.FC<ShareSocialsProps> = ({
  title = "Care Indeed",
  className,
  hashtag = "careindeed",
  shareText = "Share Event:",
  color,
}) => {
  const pathname = usePathname();
  const socialStyle = shareSocialsVariant({ color });

  const shareLinks = [
    {
      href: `https://www.facebook.com/dialog/share${qs.stringify(
        {
          display: "popup",
          hashtag: `#${hashtag}`,
          href: withUrl(pathname),
        },
        { addQueryPrefix: true }
      )}`,
      ariaLabel: "Share on Facebook",
      Icon: FaFacebookF,
    },
    {
      href: `https://twitter.com/intent/tweet${qs.stringify(
        {
          text: `${title} #${hashtag}`,
          url: withUrl(pathname),
        },
        { addQueryPrefix: true }
      )}`,
      ariaLabel: "Share on Twitter",
      Icon: FaXTwitter,
    },
    {
      href: `https://www.linkedin.com/sharing/share-offsite/${qs.stringify(
        {
          url: withUrl(pathname),
        },
        { addQueryPrefix: true }
      )}`,
      ariaLabel: "Share on LinkedIn",
      Icon: FaLinkedinIn,
    },
  ];

  return (
    <div
      className={cn("!flex !flex-row !items-center !gap-6", className)}
      role="region"
      aria-label="Share on social media"
    >
      <p className="!font-montserrat !text-subheading-3-400 !text-neutral-400 md:!text-subheading-2">
        {shareText}
      </p>
      <div className="!flex !flex-row !items-center !gap-4">
        {shareLinks.map(({ href, ariaLabel, Icon }, index) => (
          <Link
            key={`share-social-${index}`}
            href={href}
            aria-label={ariaLabel}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Icon
              className={socialStyle}
              aria-hidden="true"
            />
          </Link>
        ))}
      </div>
    </div>
  );
};

export default ShareSocials;
